import { feedCats, type FeedItem } from "@/mocks/chigua";

interface LiveFeedProps {
  category: string;
  setCategory: (v: string) => void;
  keyword: string;
  items: FeedItem[];
  total: number;
  onOpen: (item: FeedItem) => void;
}

export default function LiveFeed({ category, setCategory, keyword, items, total, onOpen }: LiveFeedProps) {
  return (
    <section id="live" className="scroll-mt-20 bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-sm font-semibold tracking-widest text-primary-500 uppercase">大瓜现场</p>
            <h2 className="mt-2 font-fun text-3xl text-foreground-950 md:text-4xl">一手热点，分类围观</h2>
            <p className="mt-2 max-w-xl text-foreground-600">
              按频道过滤全网热议事件，点击卡片可查看详细来龙去脉。想看哪口瓜，直接点进现场。
            </p>
          </div>
        </div>

        <div className="mt-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="inline-flex flex-wrap items-center gap-1 rounded-full bg-background-100 p-1 ring-1 ring-background-200">
            {feedCats.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => setCategory(c.key)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                  category === c.key
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-700 hover:bg-background-200/60"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <p className="text-sm text-foreground-500">
            {keyword ? (
              <>
                正在搜索「<span className="font-semibold text-primary-600">{keyword}</span>」
              </>
            ) : (
              <>当前共 {total} 条热点</>
            )}
            <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-accent-100 px-2.5 py-0.5 font-semibold text-accent-800">
              <i className="ri-time-line" aria-hidden="true"></i>小时级更新
            </span>
          </p>
        </div>

        {items.length === 0 ? (
          <div className="mt-8 flex flex-col items-center justify-center rounded-2xl border border-dashed border-background-300 bg-background-100/60 py-16 text-center">
            <i className="ri-forbid-2-line text-4xl text-foreground-300"></i>
            <p className="mt-3 font-semibold text-foreground-700">暂时没有匹配的瓜</p>
            <p className="mt-1 text-sm text-foreground-500">换个关键词，或切回"全部现场"再逛逛。</p>
            <button
              type="button"
              onClick={() => setCategory("all")}
              className="mt-5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              查看全部
            </button>
          </div>
        ) : (
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((item) => (
              <article
                key={item.id}
                className="group flex flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-50 transition-colors hover:border-primary-200 hover:bg-primary-50/40"
              >
                <button
                  type="button"
                  onClick={() => onOpen(item)}
                  className="relative block w-full cursor-pointer overflow-hidden text-left"
                  aria-label={`阅读：${item.title}`}
                >
                  <img
                    src={item.image}
                    alt={item.alt}
                    className="aspect-[4/3] w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                  />
                  {item.hot && (
                    <span className="absolute left-3 top-3 inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-600 px-2.5 py-1 text-xs font-bold text-background-50">
                      <i className="ri-fire-fill" aria-hidden="true"></i>火爆
                    </span>
                  )}
                </button>
                <div className="flex flex-1 flex-col p-5">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 font-semibold text-secondary-800">
                      {item.catLabel}
                    </span>
                    <span className="text-foreground-400">{item.time}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => onOpen(item)}
                    className="mt-3 text-left text-base font-bold leading-snug text-foreground-950 transition-colors hover:text-primary-600"
                  >
                    {item.title}
                  </button>
                  <p className="mt-2 line-clamp-2 text-sm text-foreground-600">{item.excerpt}</p>
                  <div className="mt-4 flex items-center justify-between pt-2">
                    <div className="flex items-center gap-3 text-xs text-foreground-500">
                      <span>
                        <i className="ri-eye-line mr-1" aria-hidden="true"></i>
                        {item.views}
                      </span>
                      <span>
                        <i className="ri-chat-3-line mr-1" aria-hidden="true"></i>
                        {item.comments}
                      </span>
                    </div>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary-600 transition-all group-hover:gap-1.5">
                      围观现场 <i className="ri-arrow-right-line" aria-hidden="true"></i>
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}